import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { format, parseISO, subDays } from "date-fns";
import { FileText, Calendar, CheckCircle, XCircle, Loader2, Printer, AlertCircle, Download, FileSpreadsheet, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export default function UserReportPage() {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const days = parseInt(searchParams.get("days") || "30");
  const reportFormat = searchParams.get("format") || "column";
  
  const [user, setUser] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  
  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailBcc, setEmailBcc] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailReportFormat, setEmailReportFormat] = useState('row'); // Default to row format
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchUserReport();
  }, [userId, days]);

  const fetchUserReport = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch user details
      const userRes = await axios.get(`${API}/users/${userId}`);
      setUser(userRes.data);
      
      // Fetch user's logs
      const logsRes = await axios.get(`${API}/admin/user-logs/${userId}?days=${days}`);
      setLogs(logsRes.data);
    } catch (err) {
      console.error("Error fetching report:", err);
      setError(err.response?.data?.detail || "Failed to load report");
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Helper to strip HTML tags
  const stripHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  };

  // Export to CSV
  const handleExportCsv = () => {
    setExportingCsv(true);
    try {
      // Build CSV content
      const headers = ["Date", "Day", "Status", "Day Type", "Tasks Today", "Challenges", "Plan Tomorrow"];
      const rows = allDates.map(dateInfo => {
        const status = dateInfo.isWeekend 
          ? "Weekend" 
          : dateInfo.log 
            ? (dateInfo.log.day_type || "Submitted") 
            : "Missing";
        return [
          dateInfo.date,
          dateInfo.displayDate,
          status,
          dateInfo.log?.day_type || "",
          `"${stripHtml(dateInfo.log?.tasks_today || "").replace(/"/g, '""')}"`,
          `"${stripHtml(dateInfo.log?.challenges || "").replace(/"/g, '""')}"`,
          `"${stripHtml(dateInfo.log?.plan_tomorrow || "").replace(/"/g, '""')}"`
        ];
      });
      
      const csvContent = [
        `Log Report for ${user?.full_name}`,
        `Email: ${user?.email}`,
        `Period: Last ${days} days`,
        `Generated: ${format(new Date(), "yyyy-MM-dd HH:mm")}`,
        `Submitted: ${submittedCount} | Missing: ${missingCount} | Rate: ${submissionRate}%`,
        "",
        headers.join(","),
        ...rows.map(row => row.join(","))
      ].join("\n");
      
      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${user?.full_name.replace(/\s+/g, "_")}_log_report_${days}days.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      
      toast.success("CSV exported successfully");
    } catch (err) {
      console.error("CSV export error:", err);
      toast.error("Failed to export CSV");
    } finally {
      setExportingCsv(false);
    }
  };

  // Export to PDF
  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(
        `${API}/admin/user-report-pdf/${userId}?days=${days}`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${user?.full_name.replace(/\s+/g, "_")}_log_report_${days}days.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      
      toast.success("PDF exported successfully");
    } catch (err) {
      console.error("PDF export error:", err);
      toast.error("Failed to export PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  const handleOpenEmailDialog = () => {
    // Pre-populate TO field with team leader emails
    const teamLeaderEmails = [];
    if (user?.teams) {
      user.teams.forEach(team => {
        if (team.leaders) {
          team.leaders.forEach(leader => {
            if (leader.email && !teamLeaderEmails.includes(leader.email)) {
              teamLeaderEmails.push(leader.email);
            }
          });
        }
      });
    }
    
    setEmailTo(teamLeaderEmails.join(', '));
    setEmailCc('');
    setEmailBcc('');
    setEmailReportFormat('row'); // Default to row format
    setEmailMessage(`Hi,\n\nPlease find attached the log report for ${user?.full_name} covering the last ${days} days.\n\nSubmission Rate: ${submissionRate}%\nSubmitted: ${submittedCount} days\nMissing: ${missingCount} days\n\nBest regards`);
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailTo.trim()) {
      toast.error('Please enter at least one recipient in the TO field');
      return;
    }

    setSendingEmail(true);
    try {
      await axios.post(`${API}/admin/user-report-email/${userId}`, {
        to: emailTo,
        cc: emailCc || null,
        bcc: emailBcc || null,
        message: emailMessage,
        days: days,
        format: emailReportFormat
      });

      toast.success('Report sent successfully');
      setEmailDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Generate date range
  const endDate = new Date();
  const startDate = subDays(endDate, days - 1);
  
  // Create a map of logs by date for easy lookup
  const logsByDate = {};
  logs.forEach(log => {
    logsByDate[log.log_date] = log;
  });

  // Generate all dates in range
  const allDates = [];
  let currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    const dateStr = format(currentDate, "yyyy-MM-dd");
    const dayOfWeek = currentDate.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    allDates.push({
      date: dateStr,
      displayDate: format(currentDate, "EEE, MMM d, yyyy"),
      isWeekend,
      log: logsByDate[dateStr] || null
    });
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Reverse to show most recent first
  allDates.reverse();

  // Calculate stats
  const weekdayDates = allDates.filter(d => !d.isWeekend);
  const submittedCount = weekdayDates.filter(d => d.log).length;
  const missingCount = weekdayDates.length - submittedCount;
  const submissionRate = weekdayDates.length > 0 
    ? Math.round((submittedCount / weekdayDates.length) * 100) 
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="mt-2 text-slate-500">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
              <h2 className="mt-4 text-lg font-semibold">Error Loading Report</h2>
              <p className="mt-2 text-slate-500">{error}</p>
              <Button onClick={fetchUserReport} className="mt-4">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6 print:p-2 print:bg-white">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 print:mb-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg print:hidden">
              <FileText className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 print:text-xl">
                Log Report: {user?.full_name}
              </h1>
              <p className="text-slate-500 print:text-sm">
                {user?.email} • Last {days} days
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <Button onClick={handleOpenEmailDialog} variant="outline">
              <Mail className="mr-2 h-4 w-4" />
              Email
            </Button>
            <Button onClick={handleExportCsv} variant="outline" disabled={exportingCsv}>
              {exportingCsv ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="mr-2 h-4 w-4" />
              )}
              CSV
            </Button>
            <Button onClick={handleExportPdf} variant="outline" disabled={exportingPdf}>
              {exportingPdf ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              PDF
            </Button>
            <Button onClick={handlePrint} variant="outline">
              <Printer className="mr-2 h-4 w-4" />
              Print
            </Button>
          </div>
        </div>

        {/* Team Info */}
        {user?.teams && user.teams.length > 0 && (
          <div className="mb-4 p-4 bg-slate-100 rounded-lg print:p-2 print:mb-2">
            <div className="flex flex-wrap gap-6">
              {user.teams.map((team) => (
                <div key={team.id} className="flex items-start gap-2">
                  <div>
                    <span className="text-sm font-medium text-slate-700">Team: </span>
                    <span className="text-sm font-semibold text-slate-900">{team.name}</span>
                    {team.leaders && team.leaders.length > 0 && (
                      <div className="mt-1">
                        <span className="text-xs text-slate-500">Team Leader: </span>
                        <span className="text-xs font-medium text-slate-700">
                          {team.leaders.map(l => l.full_name).join(", ")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 print:grid-cols-4 print:gap-2 print:mb-4">
          <Card className="print:shadow-none print:border">
            <CardContent className="pt-4 pb-4 print:p-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-400" />
                <span className="text-xs text-slate-500">Period</span>
              </div>
              <p className="text-xl font-bold mt-1 print:text-base">{days} Days</p>
            </CardContent>
          </Card>
          
          <Card className="print:shadow-none print:border">
            <CardContent className="pt-4 pb-4 print:p-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-xs text-slate-500">Submitted</span>
              </div>
              <p className="text-xl font-bold mt-1 text-green-600 print:text-base">{submittedCount}</p>
            </CardContent>
          </Card>
          
          <Card className="print:shadow-none print:border">
            <CardContent className="pt-4 pb-4 print:p-2">
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-500" />
                <span className="text-xs text-slate-500">Missing</span>
              </div>
              <p className="text-xl font-bold mt-1 text-red-600 print:text-base">{missingCount}</p>
            </CardContent>
          </Card>
          
          <Card className="print:shadow-none print:border">
            <CardContent className="pt-4 pb-4 print:p-2">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-500" />
                <span className="text-xs text-slate-500">Rate</span>
              </div>
              <p className="text-xl font-bold mt-1 text-blue-600 print:text-base">{submissionRate}%</p>
            </CardContent>
          </Card>
        </div>

        {/* Logs Table */}
        <Card className="print:shadow-none print:border">
          <CardHeader className="print:py-2">
            <CardTitle className="text-lg print:text-base flex items-center justify-between">
              <span>Daily Logs</span>
              <Badge variant="outline" className="font-normal">
                {reportFormat === "column" ? "Column Format" : "Row Format"}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="print:p-2">
            <div className="overflow-x-auto">
              {reportFormat === "column" ? (
                /* Column Format */
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-blue-800 text-white">
                      <th className="text-left py-3 px-2 font-medium print:py-1">Date</th>
                      <th className="text-left py-3 px-2 font-medium print:py-1">Status</th>
                      <th className="text-left py-3 px-2 font-medium print:py-1">Tasks Today</th>
                      <th className="text-left py-3 px-2 font-medium print:py-1">Challenges</th>
                      <th className="text-left py-3 px-2 font-medium print:py-1">Plan Tomorrow</th>
                      <th className="text-left py-3 px-2 font-medium print:py-1">AI Discovery</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allDates.map((dateInfo, idx) => (
                      <tr 
                        key={dateInfo.date} 
                        className={`border-b border-slate-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
                      >
                        <td className="py-3 px-2 whitespace-nowrap print:py-1 align-top">
                          <div>
                            <span className="font-medium">{dateInfo.displayDate}</span>
                            {dateInfo.isWeekend && (
                              <Badge variant="outline" className="ml-2 text-xs bg-slate-100">
                                Weekend
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-2 print:py-1 align-top">
                          {dateInfo.isWeekend ? (
                            <span className="text-slate-400">—</span>
                          ) : dateInfo.log ? (
                            dateInfo.log.day_type ? (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                {dateInfo.log.day_type.toUpperCase().replace('_', ' ')}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                Submitted
                              </Badge>
                            )
                          ) : (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                              Missing
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-2 print:py-1 align-top prose prose-sm max-w-none [&_p]:my-0">
                          {dateInfo.log?.tasks_today ? (
                            <div dangerouslySetInnerHTML={{ __html: dateInfo.log.tasks_today }} />
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="py-3 px-2 print:py-1 align-top prose prose-sm max-w-none [&_p]:my-0">
                          {dateInfo.log?.challenges ? (
                            <div dangerouslySetInnerHTML={{ __html: dateInfo.log.challenges }} />
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="py-3 px-2 print:py-1 align-top prose prose-sm max-w-none [&_p]:my-0">
                          {dateInfo.log?.plan_tomorrow ? (
                            <div dangerouslySetInnerHTML={{ __html: dateInfo.log.plan_tomorrow }} />
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="py-3 px-2 print:py-1 align-top prose prose-sm max-w-none [&_p]:my-0">
                          {dateInfo.log?.ai_tools_used ? (
                            <div dangerouslySetInnerHTML={{ __html: dateInfo.log.ai_tools_used }} />
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                /* Row Format */
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-blue-800 text-white">
                      <th className="text-left py-3 px-4 font-medium w-36 print:py-1">Date</th>
                      <th className="text-left py-3 px-4 font-medium print:py-1">Log Details</th>
                    </tr>
                  </thead>

                  <tbody>
                    {allDates.map((dateInfo, idx) => {
                      const fields = [
                        { label: "Tasks Worked On Today", content: dateInfo.log?.tasks_today },
                        { label: "Challenges and Hurdles", content: dateInfo.log?.challenges },
                        { label: "Plan for Tomorrow", content: dateInfo.log?.plan_tomorrow },
                        { label: "AI Discovery", content: dateInfo.log?.ai_tools_used },
                      ];
                      
                      const nonEmptyFields = fields.filter(f => f.content);
                      const isDayType = dateInfo.log?.day_type;
                      
                      return (
                        <tr 
                          key={dateInfo.date} 
                          className={`border-t border-slate-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
                        >
                          <td className="py-3 px-4 whitespace-nowrap print:py-1 align-top">
                            <div className="font-semibold">{dateInfo.displayDate}</div>
                            {dateInfo.isWeekend ? (
                              <Badge variant="outline" className="text-xs bg-slate-100 mt-1">
                                Weekend
                              </Badge>
                            ) : dateInfo.log ? (
                              isDayType ? (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs mt-1">
                                  {isDayType.toUpperCase().replace('_', ' ')}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs mt-1">
                                  Submitted
                                </Badge>
                              )
                            ) : (
                              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs mt-1">
                                Missing
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 px-4 print:py-1">
                            {dateInfo.isWeekend ? (
                              <span className="text-slate-400 italic">Weekend - No log required</span>
                            ) : isDayType ? (
                              <span className="text-amber-700 italic">{isDayType.toUpperCase().replace('_', ' ')} - No work log required</span>
                            ) : nonEmptyFields.length > 0 ? (
                              <div className="space-y-4">
                                {nonEmptyFields.map((field, fieldIdx) => (
                                  <div key={fieldIdx}>
                                    <div className="font-semibold text-blue-700 mb-1">{field.label}</div>
                                    <div className="prose prose-sm max-w-none [&_p]:my-0" dangerouslySetInnerHTML={{ __html: field.content }} />
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-red-500 italic">No log submitted</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-4 text-center text-xs text-slate-400 print:mt-2">
          Generated on {format(new Date(), "MMMM d, yyyy 'at' h:mm a")}
        </div>
      </div>

      {/* Email Report Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              Email Report
            </DialogTitle>
            <DialogDescription>
              Send {user?.full_name}'s log report ({days} days) via email with PDF attachment
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Report Format Selection */}
            <div className="space-y-2">
              <Label>Report Format</Label>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant={emailReportFormat === 'column' ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => setEmailReportFormat('column')}
                >
                  Column Format
                </Button>
                <Button
                  type="button"
                  variant={emailReportFormat === 'row' ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => setEmailReportFormat('row')}
                >
                  Row Format
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                {emailReportFormat === 'column' 
                  ? 'Shows each field in a separate column' 
                  : 'Shows each field in a separate row'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email-to">To *</Label>
              <Input
                id="email-to"
                type="email"
                placeholder="recipient@example.com"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
              />
              <p className="text-xs text-slate-500">
                Separate multiple emails with commas
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-cc">CC</Label>
              <Input
                id="email-cc"
                type="email"
                placeholder="cc@example.com (optional)"
                value={emailCc}
                onChange={(e) => setEmailCc(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-bcc">BCC</Label>
              <Input
                id="email-bcc"
                type="email"
                placeholder="bcc@example.com (optional)"
                value={emailBcc}
                onChange={(e) => setEmailBcc(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-message">Message</Label>
              <Textarea
                id="email-message"
                rows={6}
                placeholder="Enter your message..."
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
              />
            </div>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Attachment:</strong> PDF report for {user?.full_name} ({days} days) will be attached automatically
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)} disabled={sendingEmail}>
              Cancel
            </Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail}>
              {sendingEmail ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
